import React, { useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import type { CloudConfig } from '../../../../common/constants/filters/cloud-config/types';
import type { SearchStoreExperience } from '../../../../common/constants/search';
import {
	useApplicationMode,
	useAutoSelectPrimaryProduct,
	useBootstrap,
	useSetQueryParams,
} from '../../bootstrap';
import { allFilters } from '../filters';
import { SearchStoreContainer } from '../index';

export const SearchStoreProvider = ({
	children,
	experience = 'search-page',
}: {
	children: React.ReactNode;
	experience?: SearchStoreExperience;
}): React.JSX.Element => {
	const [shouldAutoSelectPrimaryProduct, setShouldAutoSelectPrimaryProduct] = useState(true);
	const intl = useIntl();

	const [
		{
			queryParams,
			cloudId,
			aggAbsoluteUrl,
			initialSpaceARIs,
			user,
			products,
			allFilterExcludedProducts,
			primaryProduct,
			experimentConfig,
			productEdition,
			productDataRegions,
			thirdPartyConfigs,
		},
	] = useBootstrap();
	const setQueryParams = useSetQueryParams();

	const applicationMode = useApplicationMode();

	const primaryProductToAutoSelect = useAutoSelectPrimaryProduct();

	const config = useMemo(
		() =>
			({
				aggAbsoluteUrl,
				ari: '',
				cloudId,
				experimentConfig,
				initialSpaceARIs,
				intl,
				productDataRegions,
				productEdition,
				userId: user?.id || '',
				product: queryParams?.product,
				primaryProduct,
				thirdPartyConfigs,
			}) satisfies CloudConfig,
		[
			aggAbsoluteUrl,
			cloudId,
			experimentConfig,
			initialSpaceARIs,
			intl,
			productDataRegions,
			productEdition,
			user?.id,
			queryParams?.product,
			primaryProduct,
			thirdPartyConfigs,
		],
	);

	const queryParamsInUse = useMemo(() => {
		const { lastModified_lt, lastModified_gt, ...queryParamsInUse } = queryParams;
		return queryParamsInUse;
	}, [queryParams]);

	// primaryProductToAutoSelect will be set to the primary product if the user is in unified search
	// and no product is selected
	// This is to ensure that the primary product is selected by default for unified search
	useEffect(() => {
		if (products && products.length > 0) {
			if (shouldAutoSelectPrimaryProduct && primaryProductToAutoSelect) {
				setQueryParams({ ...queryParams, product: primaryProductToAutoSelect });
			}
			setShouldAutoSelectPrimaryProduct(false);
		}
	}, [
		applicationMode,
		primaryProduct,
		primaryProductToAutoSelect,
		products,
		queryParams,
		setQueryParams,
		shouldAutoSelectPrimaryProduct,
	]);

	return (
		<SearchStoreContainer
			queryParams={queryParamsInUse}
			allFilters={allFilters}
			config={config}
			setQueryParams={setQueryParams}
			availableProducts={products}
			allFilterExcludedProducts={allFilterExcludedProducts}
			applicationMode={applicationMode}
			primaryProduct={primaryProduct}
			thirdPartyConfigs={thirdPartyConfigs}
			experience={experience}
		>
			{children}
		</SearchStoreContainer>
	);
};
